import { safeFetch, resolveApiUrl } from '../api.js';

let isAdminChecked = false;
let isAdmin = false;
let isSuperAdminChecked = false;
let isSuperAdmin = false;
let buildAuthHeadersFn = null;

/**
 * Установка функции для получения заголовков авторизации
 */
export function setBuildAuthHeaders(fn) {
    buildAuthHeadersFn = fn;
}

/**
 * Проверка прав администратора
 */
export async function checkAdminStatus() {
    if (isAdminChecked) {
        return isAdmin;
    }
    
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set, cannot check admin status.');
        return false;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/whitelist`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        isAdmin = response.success === true;
        isAdminChecked = true;
        
        // Проверяем, является ли текущий пользователь супер-админом
        if (isAdmin && response.data) {
            const payload = response.data.data || response.data;
            const users = payload.users || [];
            // Находим текущего пользователя в списке
            const currentUser = users.find(u => {
                // Нужно получить telegram_id текущего пользователя
                // Это можно сделать через buildAuthHeaders или другой способ
                return false; // Временно, нужно будет получить telegram_id
            });
            // Пока просто проверяем через API при загрузке списка
        }
        
        return isAdmin;
    } catch (error) {
        console.error('[Admin] Error checking admin status:', error);
        isAdmin = false;
        isAdminChecked = true;
        return false;
    }
}

/**
 * Проверка, является ли текущий пользователь супер-админом
 */
export async function checkSuperAdminStatus() {
    if (isSuperAdminChecked) {
        return isSuperAdmin;
    }
    
    if (!buildAuthHeadersFn) {
        return false;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/whitelist`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        if (response.success && response.data) {
            const payload = response.data.data || response.data;
            const users = payload.users || [];
            // Получаем telegram_id из заголовков или другим способом
            // Пока используем простую проверку - если пользователь в списке и is_super_admin = true
            // Нужно будет получить telegram_id текущего пользователя
            // Для начала просто проверяем, есть ли супер-админы в списке
            const hasSuperAdmin = users.some(u => u.is_super_admin === true);
            isSuperAdmin = hasSuperAdmin; // Временно, нужно будет проверить конкретного пользователя
        }
        
        isSuperAdminChecked = true;
        return isSuperAdmin;
    } catch (error) {
        console.error('[Admin] Error checking super admin status:', error);
        isSuperAdmin = false;
        isSuperAdminChecked = true;
        return false;
    }
}

/**
 * Получить список пользователей в whitelist
 */
async function getWhitelistUsers() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return { users: [], currentUser: null };
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/whitelist`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        if (response.success && response.data) {
            // API возвращает { success: true, data: { users, count, currentUser } }
            // safeFetch оборачивает это в { success: true, data: { success: true, data: {...} } }
            // Поэтому извлекаем внутренние data, как и в getUserStats / getAccessAttempts
            const payload = response.data.data || response.data;
            const users = payload.users || [];
            const currentUser = payload.currentUser || null;
            
            // Логируем для отладки
            console.log('[Admin] getWhitelistUsers - получено пользователей:', users.length);
            console.log('[Admin] getWhitelistUsers - текущий пользователь:', currentUser);
            
            return {
                users: users,
                currentUser: currentUser
            };
            users.forEach(user => {
                const isAdminValue = user.is_admin;
                const isAdminType = typeof isAdminValue;
                const isAdminString = String(isAdminValue);
                const isAdminCheck = isAdminValue === true || 
                                   isAdminValue === 'true' || 
                                   isAdminValue === 't' || 
                                   isAdminValue === 1 ||
                                   String(isAdminValue).toLowerCase() === 'true';
                console.log(`[Admin] User ${user.telegram_id} (@${user.username || 'N/A'}):`, {
                    is_admin: isAdminValue,
                    type: isAdminType,
                    string: isAdminString,
                    willShowCrown: isAdminCheck
                });
            });
            return users;
        }
        return { users: [], currentUser: null };
    } catch (error) {
        console.error('[Admin] Error fetching whitelist:', error);
        return { users: [], currentUser: null };
    }
}

/**
 * Получить статистику БД
 */
async function getDatabaseStats() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return null;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/monitoring`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        if (response.success) {
            return response.data || null;
        }
        return null;
    } catch (error) {
        console.error('[Admin] Error fetching database stats:', error);
        return null;
    }
}

/**
 * Получить попытки доступа
 */
async function getAccessAttempts() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return { attempts: [], stats: {}, uniqueUsers: [] };
    }
    
    try {
        const apiUrl = resolveApiUrl();
        // Фильтруем только попытки с error_type = 'access_denied' (не прошли whitelist)
        const response = await safeFetch(`${apiUrl}/admin/access-attempts?errorType=access_denied`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });

        if (response.success && response.data) {
            // API возвращает { success: true, data: { attempts, stats, uniqueUsers, pagination } }
            // safeFetch оборачивает это в { success: true, data: { success: true, data: {...} } }
            // Поэтому извлекаем внутренние data, как и в getUserStats / getBattleStats
            const payload = response.data.data || response.data;
            // Дополнительно фильтруем на фронтенде (на всякий случай)
            if (payload.attempts) {
                payload.attempts = payload.attempts.filter(a => a.error_type === 'access_denied');
            }
            return payload;
        }
        return { attempts: [], stats: {}, uniqueUsers: [] };
    } catch (error) {
        console.error('[Admin] Error fetching access attempts:', error);
        return { attempts: [], stats: {}, uniqueUsers: [] };
    }
}

/**
 * Получить статистику пользователей
 */
async function getUserStats() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return null;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/users/stats`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        console.log('[Admin] getUserStats response:', response);
        
        if (response.success && response.data) {
            // API возвращает { success: true, data: stats }
            // safeFetch оборачивает это в { success: true, data: { success: true, data: stats } }
            // Поэтому нужно извлечь response.data.data
            const stats = response.data.data || response.data;
            console.log('[Admin] User stats data:', {
                totalUsers: stats.totalUsers,
                topByCoinsLength: stats.topByCoins?.length,
                topByStarsLength: stats.topByStars?.length,
                topByCreaturesLength: stats.topByCreatures?.length,
                stats: stats
            });
            return stats;
        }
        console.warn('[Admin] getUserStats: response.success =', response.success, 'response.data =', response.data);
        return null;
    } catch (error) {
        console.error('[Admin] Error fetching user stats:', error);
        return null;
    }
}

/**
 * Получить статистику битв
 */
async function getBattleStats() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return null;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/battles/stats`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        if (response.success && response.data) {
            // API возвращает { success: true, data: stats }
            // safeFetch оборачивает это в { success: true, data: { success: true, data: stats } }
            return response.data.data || response.data;
        }
        return null;
    } catch (error) {
        console.error('[Admin] Error fetching battle stats:', error);
        return null;
    }
}

/**
 * Получить статистику подарков
 */
async function getGiftStats() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return null;
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/gifts/stats`, {
            method: 'GET',
            headers: buildAuthHeadersFn()
        });
        
        if (response.success && response.data) {
            // API возвращает { success: true, data: stats }
            // safeFetch оборачивает это в { success: true, data: { success: true, data: stats } }
            return response.data.data || response.data;
        }
        return null;
    } catch (error) {
        console.error('[Admin] Error fetching gift stats:', error);
        return null;
    }
}

/**
 * Сбросить статистику БД
 */
async function resetDatabaseStats() {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return { success: false, message: 'Ошибка авторизации' };
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/monitoring/reset`, {
            method: 'POST',
            headers: buildAuthHeadersFn()
        });
        
        return response;
    } catch (error) {
        console.error('[Admin] Error resetting database stats:', error);
        return { success: false, message: error.message || 'Ошибка сброса статистики' };
    }
}

/**
 * Добавить пользователя в whitelist
 */
async function addUserToWhitelist(telegramId, username = null, isAdmin = false) {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return { success: false, message: 'Ошибка авторизации' };
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/whitelist/add`, {
            method: 'POST',
            headers: {
                ...buildAuthHeadersFn(),
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({ telegramId, username, isAdmin })
        });
        
        return response;
    } catch (error) {
        console.error('[Admin] Error adding user to whitelist:', error);
        return { success: false, message: error.message || 'Ошибка добавления пользователя' };
    }
}

/**
 * Удалить пользователя из whitelist
 */
async function removeUserFromWhitelist(telegramId) {
    if (!buildAuthHeadersFn) {
        console.warn('[Admin] buildAuthHeaders not set');
        return { success: false, message: 'Ошибка авторизации' };
    }
    
    try {
        const apiUrl = resolveApiUrl();
        const response = await safeFetch(`${apiUrl}/admin/whitelist/remove/${telegramId}`, {
            method: 'DELETE',
            headers: buildAuthHeadersFn()
        });
        
        return response;
    } catch (error) {
        console.error('[Admin] Error removing user from whitelist:', error);
        return { success: false, message: error.message || 'Ошибка удаления пользователя' };
    }
}

/**
 * Рендеринг таба Whitelist
 */
function renderWhitelistTab(users, isCurrentUserSuperAdmin = false) {
    return `
        <div id="admin-whitelist-content">
            <div style="margin-bottom: 24px;">
                <h3 style="margin-bottom: 12px; font-size: 18px;">Добавить пользователя</h3>
                <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px;">
                    <input 
                        type="text" 
                        id="admin-add-telegram-id" 
                        placeholder="Telegram ID" 
                        style="padding: 12px; border-radius: 8px; border: 1px solid var(--tg-theme-hint-color, #999); background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color, #000);"
                    />
                    <input 
                        type="text" 
                        id="admin-add-username" 
                        placeholder="Username (опционально)" 
                        style="padding: 12px; border-radius: 8px; border: 1px solid var(--tg-theme-hint-color, #999); background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color, #000);"
                    />
                    ${isCurrentUserSuperAdmin ? `
                    <label style="display: flex; align-items: center; gap: 8px; cursor: pointer;">
                        <input 
                            type="checkbox" 
                            id="admin-add-is-admin" 
                            style="width: 20px; height: 20px;"
                        />
                        <span>Администратор</span>
                    </label>
                    ` : '<p style="font-size: 12px; color: var(--tg-theme-hint-color);">Только супер-администратор может добавлять администраторов</p>'}
                    <button 
                        id="admin-add-button" 
                        style="padding: 12px 24px; border-radius: 8px; background: var(--tg-theme-button-color, #3390ec); color: var(--tg-theme-button-text-color, #fff); border: none; font-weight: 600; cursor: pointer;"
                    >
                        Добавить
                    </button>
                </div>
                <div id="admin-add-message" style="margin-top: 12px; min-height: 20px;"></div>
            </div>
            
            <div>
                <h3 style="margin-bottom: 12px; font-size: 18px;">Пользователи в whitelist (${users.length})</h3>
                <div id="admin-users-list" style="display: flex; flex-direction: column; gap: 12px;">
                    ${users.length === 0 
                        ? '<p style="color: var(--tg-theme-hint-color); text-align: center; padding: 24px;">Список пуст</p>'
                        : users.map(user => {
                            // Определяем, является ли пользователь админом
                            const isAdminRaw = user.is_admin;
                            const isAdmin = isAdminRaw === true || 
                                          isAdminRaw === 'true' || 
                                          isAdminRaw === 't' || 
                                          isAdminRaw === 1 ||
                                          String(isAdminRaw).toLowerCase() === 'true';
                            
                            // Отладочный вывод для пользователя 5374904178
                            if (user.telegram_id === '5374904178') {
                                console.log('[Admin] DEBUG 5374904178:', {
                                    is_admin: isAdminRaw,
                                    type: typeof isAdminRaw,
                                    string: String(isAdminRaw),
                                    isAdmin: isAdmin,
                                    fullUser: user
                                });
                            }
                            
                            return `
                            <div 
                                style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999); display: flex; justify-content: space-between; align-items: center;"
                            >
                                <div>
                                    <div style="font-weight: 600; margin-bottom: 4px;">
                                        ${user.username ? `@${user.username}` : 'Без username'}
                                        ${isAdmin ? ' 👑' : ''}
                                        ${(user.is_super_admin === true || user.is_super_admin === 'true') ? ' ⭐' : ''}
                                    </div>
                                    <div style="font-size: 14px; color: var(--tg-theme-hint-color);">
                                        Telegram ID: ${user.telegram_id}
                                    </div>
                                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-top: 4px;">
                                        Добавлен: ${new Date(user.added_at).toLocaleDateString('ru-RU')}
                                    </div>
                                </div>
                                <div style="display: flex; gap: 8px; align-items: center;">
                                    ${isCurrentUserSuperAdmin ? (isAdmin 
                                        ? `<button 
                                            class="admin-toggle-admin-button" 
                                            data-telegram-id="${user.telegram_id}"
                                            data-action="remove"
                                            style="padding: 8px 16px; border-radius: 8px; background: #f59e0b; color: #fff; border: none; font-weight: 600; cursor: pointer; font-size: 12px;"
                                            title="Снять права администратора"
                                        >
                                            Снять админку
                                        </button>`
                                        : `<button 
                                            class="admin-toggle-admin-button" 
                                            data-telegram-id="${user.telegram_id}"
                                            data-action="add"
                                            style="padding: 8px 16px; border-radius: 8px; background: #10b981; color: #fff; border: none; font-weight: 600; cursor: pointer; font-size: 12px;"
                                            title="Выдать права администратора"
                                        >
                                            Сделать админом
                                        </button>`
                                    ) : ''}
                                    ${(() => {
                                        // Проверяем, можно ли удалить этого пользователя
                                        const isUserAdmin = isAdmin;
                                        const isUserSuperAdmin = user.is_super_admin === true || user.is_super_admin === 'true';
                                        
                                        // Никто не может удалить супер-админа
                                        if (isUserSuperAdmin) {
                                            return '<span style="font-size: 12px; color: var(--tg-theme-hint-color);">Супер-админ</span>';
                                        }
                                        
                                        // Только супер-админ может удалять админов
                                        if (isUserAdmin && !isCurrentUserSuperAdmin) {
                                            return '<span style="font-size: 12px; color: var(--tg-theme-hint-color);">Только супер-админ</span>';
                                        }
                                        
                                        // Обычные пользователи и админы (для супер-админа) - можно удалить
                                        return `<button 
                                            class="admin-remove-button" 
                                            data-telegram-id="${user.telegram_id}"
                                            style="padding: 8px 16px; border-radius: 8px; background: #ef4444; color: #fff; border: none; font-weight: 600; cursor: pointer;"
                                        >
                                            Удалить
                                        </button>`;
                                    })()}
                                </div>
                            </div>
                        `;
                        }).join('')
                    }
                </div>
            </div>
        </div>
    `;
}

/**
 * Рендеринг таба Статистика пользователей
 */
function renderUsersStatsTab(stats) {
    console.log('[Admin] renderUsersStatsTab called with stats:', stats);
    if (!stats) {
        return `
            <div id="admin-users-stats-content">
                <div style="padding: 24px; text-align: center; color: var(--tg-theme-hint-color);">
                    Загрузка статистики...
                </div>
            </div>
        `;
    }
    
    console.log('[Admin] Rendering stats:', {
        totalUsers: stats.totalUsers,
        topByCoins: stats.topByCoins?.length,
        topByStars: stats.topByStars?.length,
        topByCreatures: stats.topByCreatures?.length
    });

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return num.toLocaleString('ru-RU');
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return `
        <div id="admin-users-stats-content">
            <div style="margin-bottom: 24px;">
                <h3 style="font-size: 18px; margin-bottom: 16px;">Общая статистика</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 24px;">
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего пользователей</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(stats.totalUsers || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Активных (24ч)</div>
                        <div style="font-size: 24px; font-weight: 600; color: #10b981;">${formatNumber(stats.activeUsers || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Новых (7 дней)</div>
                        <div style="font-size: 24px; font-weight: 600; color: #3b82f6;">${formatNumber(stats.newUsers || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего монет</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(stats.balanceStats?.totalCoins || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего звёзд</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(stats.balanceStats?.totalStars || 0)}</div>
                    </div>
                </div>

                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 16px;">
                    <div>
                        <h4 style="margin-bottom: 12px; font-size: 16px;">🏆 Топ по монетам</h4>
                        <div style="display: flex; flex-direction: column; gap: 8px;">
                            ${(stats.topByCoins && stats.topByCoins.length > 0) 
                                ? stats.topByCoins.slice(0, 5).map((user, idx) => `
                                    <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                        <div style="display: flex; justify-content: space-between; align-items: center;">
                                            <div>
                                                <div style="font-weight: 600; margin-bottom: 4px;">
                                                    ${idx + 1}. ${user.username || `Игрок #${user.id}`}
                                                </div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                                                    ID: ${user.telegramId || user.id}
                                                </div>
                                            </div>
                                            <div style="text-align: right;">
                                                <div style="font-size: 18px; font-weight: 600;">🪙 ${formatNumber(user.coins || 0)}</div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">⭐ ${formatNumber(user.stars || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                `).join('')
                                : '<p style="color: var(--tg-theme-hint-color); text-align: center; padding: 12px;">Нет данных</p>'
                            }
                        </div>
                    </div>

                    <div>
                        <h4 style="margin-bottom: 12px; font-size: 16px;">⭐ Топ по звёздам</h4>
                        <div style="display: flex; flex-direction: column; gap: 8px;">
                            ${(stats.topByStars && stats.topByStars.length > 0)
                                ? stats.topByStars.slice(0, 5).map((user, idx) => `
                                    <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                        <div style="display: flex; justify-content: space-between; align-items: center;">
                                            <div>
                                                <div style="font-weight: 600; margin-bottom: 4px;">
                                                    ${idx + 1}. ${user.username || `Игрок #${user.id}`}
                                                </div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                                                    ID: ${user.telegramId || user.id}
                                                </div>
                                            </div>
                                            <div style="text-align: right;">
                                                <div style="font-size: 18px; font-weight: 600;">⭐ ${formatNumber(user.stars || 0)}</div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">🪙 ${formatNumber(user.coins || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                `).join('')
                                : '<p style="color: var(--tg-theme-hint-color); text-align: center; padding: 12px;">Нет данных</p>'
                            }
                        </div>
                    </div>

                    <div>
                        <h4 style="margin-bottom: 12px; font-size: 16px;">🐾 Топ по существам</h4>
                        <div style="display: flex; flex-direction: column; gap: 8px;">
                            ${(stats.topByCreatures && stats.topByCreatures.length > 0)
                                ? stats.topByCreatures.slice(0, 5).map((user, idx) => `
                                    <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                        <div style="display: flex; justify-content: space-between; align-items: center;">
                                            <div>
                                                <div style="font-weight: 600; margin-bottom: 4px;">
                                                    ${idx + 1}. ${user.username || `Игрок #${user.id}`}
                                                </div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                                                    ID: ${user.telegramId || user.id}
                                                </div>
                                            </div>
                                            <div style="text-align: right;">
                                                <div style="font-size: 18px; font-weight: 600;">🐾 ${formatNumber(user.creatureCount || 0)}</div>
                                            </div>
                                        </div>
                                    </div>
                                `).join('')
                                : '<p style="color: var(--tg-theme-hint-color); text-align: center; padding: 12px;">Нет данных</p>'
                            }
                        </div>
                    </div>
                </div>
            </div>
        </div>
    `;
}

/**
 * Рендеринг таба Мониторинг БД
 */
function renderMonitoringTab(stats) {
    if (!stats) {
        return `
            <div id="admin-monitoring-content">
                <div style="padding: 24px; text-align: center; color: var(--tg-theme-hint-color);">
                    Загрузка статистики...
                </div>
            </div>
        `;
    }

    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return num.toLocaleString('ru-RU');
    };

    const formatDuration = (ms) => {
        if (!ms && ms !== 0) return '0 мс';
        if (ms < 1000) return `${Math.round(ms)} мс`;
        if (ms < 60000) return `${(ms / 1000).toFixed(2)} с`;
        return `${(ms / 60000).toFixed(2)} мин`;
    };

    // Извлекаем данные из stats (структура: { pool, queries, slowQueries, tables })
    const queryStats = stats.queries || {};
    const poolStats = stats.pool || {};
    const slowQueries = stats.slowQueries || [];

    return `
        <div id="admin-monitoring-content">
            <div style="margin-bottom: 24px;">
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 16px;">
                    <h3 style="font-size: 18px; margin: 0;">Статистика БД</h3>
                    <button 
                        id="admin-reset-stats-button"
                        style="padding: 8px 16px; border-radius: 8px; background: #f59e0b; color: #fff; border: none; font-weight: 600; cursor: pointer; font-size: 14px;"
                    >
                        🔄 Сбросить
                    </button>
                </div>
                
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 12px; margin-bottom: 24px;">
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего запросов</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(queryStats.totalQueries || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Средняя длительность</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatDuration(queryStats.avgTime || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Ошибок</div>
                        <div style="font-size: 24px; font-weight: 600; color: #ef4444;">${formatNumber(queryStats.errors || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Медленных запросов</div>
                        <div style="font-size: 24px; font-weight: 600; color: #f59e0b;">${formatNumber(slowQueries.length || 0)}</div>
                    </div>
                </div>

                ${slowQueries && slowQueries.length > 0 ? `
                    <div style="margin-bottom: 24px;">
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Топ медленных запросов</h4>
                        <div style="display: flex; flex-direction: column; gap: 8px;">
                            ${slowQueries.slice(0, 10).map((query, idx) => `
                                <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                    <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">
                                        #${idx + 1} • ${formatDuration(query.meanTime || query.duration || 0)} • ${formatNumber(query.count || 0)} раз
                                    </div>
                                    <div style="font-size: 11px; font-family: monospace; color: var(--tg-theme-text-color); word-break: break-all;">
                                        ${(query.query || '').substring(0, 200)}${(query.query || '').length > 200 ? '...' : ''}
                                    </div>
                                </div>
                            `).join('')}
                        </div>
                    </div>
                ` : ''}

                ${poolStats && (poolStats.total !== undefined || poolStats.active !== undefined) ? `
                    <div>
                        <h4 style="margin-bottom: 12px; font-size: 16px;">Статистика пула соединений</h4>
                        <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px;">
                            <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                <div style="font-size: 11px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего</div>
                                <div style="font-size: 20px; font-weight: 600;">${formatNumber(poolStats.total || 0)}</div>
                            </div>
                            <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                <div style="font-size: 11px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Активных</div>
                                <div style="font-size: 20px; font-weight: 600;">${formatNumber(poolStats.active || 0)}</div>
                            </div>
                            <div style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                <div style="font-size: 11px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Ожидающих</div>
                                <div style="font-size: 20px; font-weight: 600;">${formatNumber(poolStats.waiting || 0)}</div>
                            </div>
                        </div>
                    </div>
                ` : ''}
            </div>
        </div>
    `;
}

/**
 * Рендеринг таба пополнения баланса
 */
/**
 * Рендеринг таба попыток доступа
 */
function renderAccessAttemptsTab(data) {
    const { attempts = [], stats = {}, uniqueUsers = [] } = data;
    
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        });
    };
    
    const formatRelativeTime = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);
        
        if (diffMins < 1) return 'только что';
        if (diffMins < 60) return `${diffMins} ${diffMins === 1 ? 'минуту' : diffMins < 5 ? 'минуты' : 'минут'} назад`;
        if (diffHours < 24) return `${diffHours} ${diffHours === 1 ? 'час' : diffHours < 5 ? 'часа' : 'часов'} назад`;
        if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'день' : diffDays < 5 ? 'дня' : 'дней'} назад`;
        return formatDate(dateString);
    };
    
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '0';
        return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    };
    
    // Группируем попытки по telegram_id для подсчета количества
    const attemptsByUser = {};
    attempts.forEach(attempt => {
        const key = attempt.telegram_id;
        if (!attemptsByUser[key]) {
            attemptsByUser[key] = [];
        }
        attemptsByUser[key].push(attempt);
    });
    
    return `
        <div id="admin-access-attempts-content">
            <div style="margin-bottom: 24px;">
                <h3 style="font-size: 18px; margin-bottom: 16px;">📊 Статистика попыток доступа</h3>
                <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 12px; margin-bottom: 24px;">
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Всего попыток</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(stats.total || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">Уникальных пользователей</div>
                        <div style="font-size: 24px; font-weight: 600;">${formatNumber(stats.uniqueUsers || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">За 24 часа</div>
                        <div style="font-size: 24px; font-weight: 600; color: #ef4444;">${formatNumber(stats.last24h || 0)}</div>
                    </div>
                    <div style="padding: 16px; border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                        <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 4px;">За 7 дней</div>
                        <div style="font-size: 24px; font-weight: 600; color: #f59e0b;">${formatNumber(stats.last7d || 0)}</div>
                    </div>
                </div>
            </div>
            
            <div style="margin-bottom: 16px;">
                <input 
                    type="text" 
                    id="access-attempts-search" 
                    placeholder="🔍 Поиск по username, ID или IP..." 
                    style="width: 100%; padding: 12px; border-radius: 8px; border: 1px solid var(--tg-theme-hint-color, #999); background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color, #000); font-size: 14px;"
                />
            </div>
            
            <div>
                <h3 style="font-size: 18px; margin-bottom: 16px;">📋 Попытки входа (не в whitelist)</h3>
                <p style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 12px;">
                    Показываются только те, кого не пустило из-за отсутствия в whitelist
                </p>
                <div id="access-attempts-list" style="display: flex; flex-direction: column; gap: 8px; max-height: 500px; overflow-y: auto;">
                    ${attempts.length > 0
                        ? (() => {
                            // Группируем попытки по telegram_id, показываем только первую попытку каждого пользователя
                            const seenUsers = new Set();
                            return attempts.map((attempt) => {
                                const userId = attempt.telegram_id;
                                const userAttempts = attemptsByUser[userId] || [];
                                const attemptCount = userAttempts.length;
                                const isFirstSeen = !seenUsers.has(userId);
                                
                                if (isFirstSeen) {
                                    seenUsers.add(userId);
                                    return `
                                    <div class="access-attempt-item" data-telegram-id="${attempt.telegram_id}" data-username="${(attempt.username || '').toLowerCase()}" data-ip="${attempt.ip_address || ''}" style="padding: 12px; border-radius: 8px; background: var(--tg-theme-secondary-bg-color, #f1f1f1); border: 1px solid var(--tg-theme-hint-color, #999);">
                                        <div style="display: flex; justify-content: space-between; align-items: start; margin-bottom: 8px;">
                                            <div style="flex: 1;">
                                                <div style="font-weight: 600; margin-bottom: 4px; display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
                                                    ${attempt.first_name || attempt.username || `Пользователь #${attempt.telegram_id}`}
                                                    ${attemptCount > 1 ? `<span style="font-size: 11px; padding: 2px 6px; border-radius: 4px; background: #fef3c7; color: #92400e;">${attemptCount} попыток</span>` : ''}
                                                </div>
                                                <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                                                    @${attempt.username || 'N/A'} | ID: ${attempt.telegram_id}
                                                </div>
                                            </div>
                                            <div style="text-align: right; display: flex; flex-direction: column; gap: 8px; align-items: flex-end;">
                                                <div>
                                                    <div style="font-size: 12px; color: var(--tg-theme-hint-color);">
                                                        ${formatRelativeTime(attempt.created_at)}
                                                    </div>
                                                    <div style="font-size: 10px; color: var(--tg-theme-hint-color); opacity: 0.7;">
                                                        ${formatDate(attempt.created_at)}
                                                    </div>
                                                </div>
                                                <div style="font-size: 11px; padding: 2px 6px; border-radius: 4px; background: #fee2e2; color: #991b1b;">
                                                    Нет доступа
                                                </div>
                                            </div>
                                        </div>
                                        <div style="font-size: 11px; color: var(--tg-theme-hint-color); margin-bottom: 8px;">
                                            <div>🌐 IP: ${attempt.ip_address || 'N/A'}</div>
                                            <div>📍 Path: ${attempt.path || 'N/A'}</div>
                                            ${attempt.user_agent ? `<div style="margin-top: 4px; opacity: 0.8;">📱 ${attempt.user_agent.length > 50 ? attempt.user_agent.substring(0, 50) + '...' : attempt.user_agent}</div>` : ''}
                                        </div>
                                        <button 
                                            class="access-attempt-add-button" 
                                            data-telegram-id="${attempt.telegram_id}" 
                                            data-username="${attempt.username || ''}"
                                            style="width: 100%; padding: 8px 16px; border-radius: 6px; background: var(--tg-theme-button-color, #3390ec); color: var(--tg-theme-button-text-color, #fff); border: none; font-weight: 600; cursor: pointer; font-size: 13px; margin-top: 8px;"
                                        >
                                            ➕ Добавить в whitelist
                                        </button>
                                    </div>
                                    `;
                                }
                                return '';
                            }).filter(Boolean).join('');
                        })()
                        : '<p style="color: var(--tg-theme-hint-color); text-align: center; padding: 12px;">Нет попыток входа от пользователей не в whitelist</p>'
                    }
                </div>
            </div>
        </div>
    `;
}

function renderBalanceTab() {
    return `
        <div id="admin-balance-content">
            <div style="margin-bottom: 24px;">
                <h3 style="margin-bottom: 12px; font-size: 18px;">Поиск пользователя</h3>
                <div style="display: flex; gap: 8px; margin-bottom: 16px;">
                    <input 
                        type="text" 
                        id="admin-balance-search" 
                        placeholder="Username или Telegram ID"
                        style="flex: 1; padding: 10px; border: 1px solid var(--tg-theme-hint-color, #999); border-radius: 8px; background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color); font-size: 14px;"
                    />
                    <button 
                        id="admin-balance-search-button"
                        style="padding: 10px 20px; border: none; background: var(--tg-theme-button-color, #3390ec); color: var(--tg-theme-button-text-color, #fff); border-radius: 8px; cursor: pointer; font-weight: 600;"
                    >
                        🔍 Найти
                    </button>
                </div>
                <div id="admin-balance-search-results" style="margin-top: 16px;"></div>
            </div>
            
            <div id="admin-balance-form" style="display: none; margin-top: 24px; padding: 20px; border: 2px solid var(--tg-theme-hint-color, #999); border-radius: 12px; background: var(--tg-theme-secondary-bg-color, #f5f5f5);">
                <h3 style="margin-bottom: 16px; font-size: 18px;">Пополнить баланс</h3>
                <div id="admin-balance-user-info" style="margin-bottom: 16px; padding: 12px; background: var(--tg-theme-bg-color, #fff); border-radius: 8px;">
                    <!-- Информация о пользователе -->
                </div>
                <div style="display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px;">
                    <div>
                        <label style="display: block; margin-bottom: 6px; font-weight: 600; color: var(--tg-theme-text-color);">💰 Монеты (coins)</label>
                        <input 
                            type="number" 
                            id="admin-balance-coins" 
                            placeholder="0"
                            min="0"
                            style="width: 100%; padding: 10px; border: 1px solid var(--tg-theme-hint-color, #999); border-radius: 8px; background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color); font-size: 14px;"
                        />
                    </div>
                    <div>
                        <label style="display: block; margin-bottom: 6px; font-weight: 600; color: var(--tg-theme-text-color);">⭐ Звёзды (stars)</label>
                        <input 
                            type="number" 
                            id="admin-balance-stars" 
                            placeholder="0"
                            min="0"
                            style="width: 100%; padding: 10px; border: 1px solid var(--tg-theme-hint-color, #999); border-radius: 8px; background: var(--tg-theme-bg-color, #fff); color: var(--tg-theme-text-color); font-size: 14px;"
                        />
                    </div>
                </div>
                <div id="admin-balance-message" style="margin-bottom: 12px; min-height: 20px;"></div>
                <button 
                    id="admin-balance-submit-button"
                    style="width: 100%; padding: 12px; border: none; background: var(--tg-theme-button-color, #3390ec); color: var(--tg-theme-button-text-color, #fff); border-radius: 8px; cursor: pointer; font-weight: 600; font-size: 16px;"
                >
                    ✅ Пополнить баланс
                </button>
            </div>
        </div>
    `;
}

/**
 * Обработчики для таба пополнения баланса
 */
function attachBalanceHandlers() {
    const searchInput = document.getElementById('admin-balance-search');
    const searchButton = document.getElementById('admin-balance-search-button');
    const searchResults = document.getElementById('admin-balance-search-results');
    const balanceForm = document.getElementById('admin-balance-form');
    const userInfo = document.getElementById('admin-balance-user-info');
    const coinsInput = document.getElementById('admin-balance-coins');
    const starsInput = document.getElementById('admin-balance-stars');
    const submitButton = document.getElementById('admin-balance-submit-button');
    const messageDiv = document.getElementById('admin-balance-message');
    
    let selectedUser = null;
    
    // Поиск по Enter
    if (searchInput) {
        searchInput.addEventListener('keypress', (e) => {
            if (e.key === 'Enter') {
                searchButton?.click();
            }
        });
    }
    
    // Поиск пользователя
    if (searchButton) {
        searchButton.addEventListener('click', async () => {
            const query = searchInput?.value?.trim();
            if (!query) {
                messageDiv.innerHTML = '<div style="color: #ff4444;">Введите username или Telegram ID</div>';
                return;
            }
            
            searchButton.disabled = true;
            searchButton.textContent = '⏳ Поиск...';
            searchResults.innerHTML = '<div style="padding: 12px; text-align: center; color: var(--tg-theme-hint-color);">Поиск...</div>';
            
            try {
                const apiUrl = resolveApiUrl();
                const response = await safeFetch(`${apiUrl}/admin/users/search?query=${encodeURIComponent(query)}`, {
                    method: 'GET',
                    headers: buildAuthHeadersFn()
                });
                
                console.log('[Admin] Ответ поиска:', response);
                
                // safeFetch возвращает { success: true, data: {...} }
                // где data - это ответ сервера
                let users = null;
                if (response.success && response.data) {
                    // Проверяем разные возможные структуры ответа
                    if (response.data.success && response.data.data && response.data.data.users) {
                        users = response.data.data.users;
                    } else if (response.data.success && response.data.users) {
                        users = response.data.users;
                    } else if (response.data.users) {
                        users = response.data.users;
                    } else if (Array.isArray(response.data)) {
                        users = response.data;
                    }
                }
                
                if (users && Array.isArray(users)) {
                    
                    if (users.length === 0) {
                        searchResults.innerHTML = '<div style="padding: 12px; text-align: center; color: var(--tg-theme-hint-color);">Пользователь не найден</div>';
                    } else if (users.length === 1) {
                        // Один пользователь - сразу показываем форму
                        selectedUser = users[0];
                        showBalanceForm(selectedUser);
                        searchResults.innerHTML = '';
                    } else {
                        // Несколько пользователей - показываем список
                        searchResults.innerHTML = `
                            <div style="display: flex; flex-direction: column; gap: 8px;">
                                ${users.map(user => `
                                    <div 
                                        class="admin-balance-user-item"
                                        style="padding: 12px; border: 1px solid var(--tg-theme-hint-color, #999); border-radius: 8px; background: var(--tg-theme-bg-color, #fff); cursor: pointer; transition: background 0.2s;"
                                        data-user-id="${user.id}"
                                        data-telegram-id="${user.telegram_id}"
                                        data-username="${user.username || ''}"
                                        data-coins="${user.coins}"
                                        data-stars="${user.stars}"
                                    >
                                        <div style="font-weight: 600;">${user.username || 'Без username'}</div>
                                        <div style="font-size: 12px; color: var(--tg-theme-hint-color);">Telegram ID: ${user.telegram_id}</div>
                                        <div style="font-size: 12px; margin-top: 4px;">
                                            💰 ${user.coins} монет | ⭐ ${user.stars} звёзд
                                        </div>
                                    </div>
                                `).join('')}
                            </div>
                        `;
                        
                        // Обработчики клика на пользователя
                        document.querySelectorAll('.admin-balance-user-item').forEach(item => {
                            item.addEventListener('click', () => {
                                selectedUser = {
                                    id: item.dataset.userId,
                                    telegram_id: item.dataset.telegramId,
                                    username: item.dataset.username,
                                    coins: parseInt(item.dataset.coins),
                                    stars: parseInt(item.dataset.stars)
                                };
                                showBalanceForm(selectedUser);
                                searchResults.innerHTML = '';
                            });
                        });
                    }
                } else {
                    searchResults.innerHTML = '<div style="padding: 12px; text-align: center; color: #ff4444;">Ошибка поиска</div>';
                }
            } catch (error) {
                console.error('[Admin] Ошибка поиска пользователя:', error);
                searchResults.innerHTML = '<div style="padding: 12px; text-align: center; color: #ff4444;">Ошибка: ' + (error.message || 'Неизвестная ошибка') + '</div>';
            } finally {
                searchButton.disabled = false;
                searchButton.textContent = '🔍 Найти';
            }
        });
    }
    
    function showBalanceForm(user) {
        selectedUser = user;
        userInfo.innerHTML = `
            <div style="font-weight: 600; margin-bottom: 8px;">${user.username || 'Без username'}</div>
            <div style="font-size: 12px; color: var(--tg-theme-hint-color); margin-bottom: 8px;">Telegram ID: ${user.telegram_id}</div>
            <div style="font-size: 14px;">
                <span style="margin-right: 16px;">💰 <strong>${user.coins}</strong> монет</span>
                <span>⭐ <strong>${user.stars}</strong> звёзд</span>
            </div>
        `;
        balanceForm.style.display = 'block';
        if (coinsInput) coinsInput.value = '';
        if (starsInput) starsInput.value = '';
        messageDiv.innerHTML = '';
    }
    
    // Пополнение баланса
    if (submitButton) {
        submitButton.addEventListener('click', async () => {
            if (!selectedUser) {
                messageDiv.innerHTML = '<div style="color: #ff4444;">Сначала выберите пользователя</div>';
                return;
            }
            
            const coins = parseInt(coinsInput?.value || '0');
            const stars = parseInt(starsInput?.value || '0');
            
            if (coins === 0 && stars === 0) {
                messageDiv.innerHTML = '<div style="color: #ff4444;">Укажите количество монет или звёзд</div>';
                return;
            }
            
            if (coins < 0 || stars < 0) {
                messageDiv.innerHTML = '<div style="color: #ff4444;">Количество не может быть отрицательным</div>';
                return;
            }
            
            submitButton.disabled = true;
            submitButton.textContent = '⏳ Обновление...';
            messageDiv.innerHTML = '';
            
            try {
                const apiUrl = resolveApiUrl();
                const response = await safeFetch(`${apiUrl}/admin/users/balance`, {
                    method: 'POST',
                    headers: {
                        ...buildAuthHeadersFn(),
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        telegramId: selectedUser.telegram_id,
                        coins: coins,
                        stars: stars
                    })
                });
                
                console.log('[Admin] Ответ пополнения баланса:', response);
                
                // safeFetch возвращает { success: true, data: {...} }
                // где data - это ответ сервера
                let updatedUser = null;
                if (response.success && response.data) {
                    // Проверяем разные возможные структуры ответа
                    if (response.data.success && response.data.data && response.data.data.user) {
                        updatedUser = response.data.data.user;
                    } else if (response.data.success && response.data.user) {
                        updatedUser = response.data.user;
                    } else if (response.data.user) {
                        updatedUser = response.data.user;
                    } else if (response.data.data && response.data.data.user) {
                        updatedUser = response.data.data.user;
                    }
                }
                
                if (updatedUser && updatedUser.coins !== undefined && updatedUser.stars !== undefined) {
                    messageDiv.innerHTML = `<div style="color: #00aa00; font-weight: 600;">✅ Баланс успешно обновлён!</div>`;
                    
                    // Обновляем информацию о пользователе
                    selectedUser.coins = updatedUser.coins;
                    selectedUser.stars = updatedUser.stars;
                    showBalanceForm(selectedUser);
                    
                    // Очищаем поля
                    if (coinsInput) coinsInput.value = '';
                    if (starsInput) starsInput.value = '';
                } else {
                    const errorMsg = response.data?.message || response.data?.error || response.message || response.error || 'Неизвестная ошибка';
                    messageDiv.innerHTML = `<div style="color: #ff4444;">Ошибка: ${errorMsg}</div>`;
                    console.error('[Admin] Неверная структура ответа:', response);
                }
            } catch (error) {
                console.error('[Admin] Ошибка пополнения баланса:', error);
                messageDiv.innerHTML = `<div style="color: #ff4444;">Ошибка: ${error.message || 'Неизвестная ошибка'}</div>`;
            } finally {
                submitButton.disabled = false;
                submitButton.textContent = '✅ Пополнить баланс';
            }
        });
    }
}

/**
 * Рендеринг экрана админ-панели
 */
export async function renderAdminScreen() {
    const container = document.getElementById('admin-screen');
    if (!container) {
        console.error('[Admin] Admin screen container not found');
        return;
    }

    // Проверяем права администратора
    const hasAdminAccess = await checkAdminStatus();
    if (!hasAdminAccess) {
        container.innerHTML = `
            <div style="padding: 24px; text-align: center;">
                <h2 style="margin-bottom: 16px;">🚫 Доступ запрещён</h2>
                <p style="color: var(--tg-theme-hint-color);">
                    У вас нет прав администратора для доступа к этой странице.
                </p>
            </div>
        `;
        return;
    }

    // Загружаем данные для всех табов
    const [whitelistData, dbStats, userStats] = await Promise.all([
        getWhitelistUsers(),
        getDatabaseStats(),
        getUserStats()
    ]);
    
    // Извлекаем пользователей и информацию о текущем пользователе
    // getWhitelistUsers теперь возвращает { users: [], currentUser: null }
    const users = (whitelistData && typeof whitelistData === 'object' && !Array.isArray(whitelistData)) 
        ? (whitelistData.users || [])
        : (Array.isArray(whitelistData) ? whitelistData : []);
    const currentUser = (whitelistData && typeof whitelistData === 'object' && !Array.isArray(whitelistData))
        ? (whitelistData.currentUser || null)
        : null;
    const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUser?.is_super_admin === 'true';
    
    // Логируем загруженных пользователей перед рендерингом
    console.log('[Admin] renderAdminScreen - загружено пользователей:', users.length);
    console.log('[Admin] renderAdminScreen - текущий пользователь:', currentUser, 'is_super_admin:', isCurrentUserSuperAdmin);
    users.forEach(user => {
        if (user.telegram_id === '5374904178' || user.telegram_id === '7033874561') {
            console.log(`[Admin] renderAdminScreen - User ${user.telegram_id}:`, {
                username: user.username,
                is_admin: user.is_admin,
                is_super_admin: user.is_super_admin,
                type: typeof user.is_admin
            });
        }
    });
    
    // Определяем активный таб (по умолчанию whitelist)
    let activeTab = 'whitelist';
    
    container.innerHTML = `
        <div style="padding: 20px;">
            <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px;">
                <h2 style="margin: 0;">⚙️ Админ-панель</h2>
                <button 
                    id="admin-refresh-button"
                    style="padding: 8px 16px; border-radius: 8px; background: var(--tg-theme-button-color, #3390ec); color: #fff; border: none; font-weight: 600; cursor: pointer; font-size: 14px; display: flex; align-items: center; gap: 6px;"
                    title="Обновить данные"
                >
                    <span id="admin-refresh-icon">🔄</span>
                    <span id="admin-refresh-text">Обновить</span>
                </button>
            </div>
            
            <!-- Табы -->
            <div style="display: flex; gap: 8px; margin-bottom: 24px; border-bottom: 2px solid var(--tg-theme-hint-color, #999); overflow-x: auto;">
                <button 
                    class="admin-tab-button" 
                    data-tab="whitelist"
                    style="padding: 12px 20px; border: none; background: transparent; color: var(--tg-theme-text-color); font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; transition: all 0.2s; white-space: nowrap;"
                >
                    👥 Whitelist
                </button>
                <button 
                    class="admin-tab-button" 
                    data-tab="users-stats"
                    style="padding: 12px 20px; border: none; background: transparent; color: var(--tg-theme-text-color); font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; transition: all 0.2s; white-space: nowrap;"
                >
                    📈 Статистика
                </button>
                <button 
                    class="admin-tab-button" 
                    data-tab="monitoring"
                    style="padding: 12px 20px; border: none; background: transparent; color: var(--tg-theme-text-color); font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; transition: all 0.2s; white-space: nowrap;"
                >
                    📊 Мониторинг БД
                </button>
                <button 
                    class="admin-tab-button" 
                    data-tab="balance"
                    style="padding: 12px 20px; border: none; background: transparent; color: var(--tg-theme-text-color); font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; transition: all 0.2s; white-space: nowrap;"
                >
                    💰 Пополнение баланса
                </button>
                <button 
                    class="admin-tab-button" 
                    data-tab="access-attempts"
                    style="padding: 12px 20px; border: none; background: transparent; color: var(--tg-theme-text-color); font-weight: 600; cursor: pointer; border-bottom: 3px solid transparent; margin-bottom: -2px; transition: all 0.2s; white-space: nowrap;"
                >
                    🚫 Попытки доступа
                </button>
            </div>
            
            <!-- Контент табов -->
            <div id="admin-tab-content">
                ${renderWhitelistTab(users, isCurrentUserSuperAdmin)}
            </div>
        </div>
    `;

    // Обработчики переключения табов
    const tabButtons = document.querySelectorAll('.admin-tab-button');
    
    tabButtons.forEach(button => {
        button.addEventListener('click', async () => {
            const tab = button.getAttribute('data-tab');
            activeTab = tab;
            
            // Обновляем стили кнопок
            tabButtons.forEach(btn => {
                const isActive = btn.getAttribute('data-tab') === tab;
                btn.style.borderBottomColor = isActive ? 'var(--tg-theme-button-color, #3390ec)' : 'transparent';
                btn.style.color = isActive ? 'var(--tg-theme-button-color, #3390ec)' : 'var(--tg-theme-text-color)';
            });
            
            // Обновляем контент
            const contentDiv = document.getElementById('admin-tab-content');
            if (tab === 'whitelist') {
                const whitelistData = await getWhitelistUsers();
                const users = whitelistData.users || [];
                const currentUser = whitelistData.currentUser || null;
                const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUser?.is_super_admin === 'true';
                contentDiv.innerHTML = renderWhitelistTab(users, isCurrentUserSuperAdmin);
                attachWhitelistHandlers();
            } else if (tab === 'users-stats') {
                const stats = await getUserStats();
                contentDiv.innerHTML = renderUsersStatsTab(stats);
            } else if (tab === 'monitoring') {
                const stats = await getDatabaseStats();
                contentDiv.innerHTML = renderMonitoringTab(stats);
                attachMonitoringHandlers();
            } else if (tab === 'balance') {
                contentDiv.innerHTML = renderBalanceTab();
                attachBalanceHandlers();
            } else if (tab === 'access-attempts') {
                const accessData = await getAccessAttempts();
                contentDiv.innerHTML = renderAccessAttemptsTab(accessData);
                attachAccessAttemptsHandlers();
            }
        });
    });

    // Устанавливаем активный таб по умолчанию
    const defaultTabButton = document.querySelector(`[data-tab="${activeTab}"]`);
    if (defaultTabButton) {
        defaultTabButton.style.borderBottomColor = 'var(--tg-theme-button-color, #3390ec)';
        defaultTabButton.style.color = 'var(--tg-theme-button-color, #3390ec)';
    }

    // Прикрепляем обработчики для текущего таба
    attachWhitelistHandlers();
    
    // Обработчик кнопки обновления
    const refreshButton = document.getElementById('admin-refresh-button');
    if (refreshButton) {
        refreshButton.addEventListener('click', async () => {
            await refreshCurrentTab(activeTab);
        });
    }
    
    // Сохраняем функцию для получения активного таба
    window.__adminRefreshFunctions = { getActiveTab: () => activeTab };
}

/**
 * Обновить текущий таб
 */
async function refreshCurrentTab(tab) {
    const refreshIcon = document.getElementById('admin-refresh-icon');
    const refreshText = document.getElementById('admin-refresh-text');
    const contentDiv = document.getElementById('admin-tab-content');
    
    if (!contentDiv) return;
    
    // Если tab не указан, используем активный таб
    if (!tab && window.__adminRefreshFunctions) {
        tab = window.__adminRefreshFunctions.getActiveTab();
    }
    
    if (!tab) {
        console.error('[Admin] refreshCurrentTab: tab не указан');
        return;
    }
    
    // Анимация обновления
    if (refreshIcon) {
        refreshIcon.style.cssText = 'animation: spin 1s linear infinite; display: inline-block;';
        refreshIcon.textContent = '⏳';
    }
    if (refreshText) {
        refreshText.textContent = 'Обновление...';
    }
    
    try {
        if (tab === 'whitelist') {
            const whitelistData = await getWhitelistUsers();
            const users = whitelistData.users || [];
            const currentUser = whitelistData.currentUser || null;
            const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUser?.is_super_admin === 'true';
            contentDiv.innerHTML = renderWhitelistTab(users, isCurrentUserSuperAdmin);
            attachWhitelistHandlers();
        } else if (tab === 'users-stats') {
            const stats = await getUserStats();
            contentDiv.innerHTML = renderUsersStatsTab(stats);
        } else if (tab === 'monitoring') {
            const stats = await getDatabaseStats();
            contentDiv.innerHTML = renderMonitoringTab(stats);
            attachMonitoringHandlers();
        } else if (tab === 'balance') {
            contentDiv.innerHTML = renderBalanceTab();
            attachBalanceHandlers();
        } else if (tab === 'access-attempts') {
            const accessData = await getAccessAttempts();
            contentDiv.innerHTML = renderAccessAttemptsTab(accessData);
            attachAccessAttemptsHandlers();
        }
        
        // Успешное обновление
        if (refreshIcon) {
            refreshIcon.style.cssText = '';
            refreshIcon.textContent = '✅';
            setTimeout(() => {
                refreshIcon.textContent = '🔄';
            }, 2000);
        }
        if (refreshText) {
            refreshText.textContent = 'Обновлено';
            setTimeout(() => {
                refreshText.textContent = 'Обновить';
            }, 2000);
        }
    } catch (error) {
        console.error('[Admin] ❌ Ошибка при обновлении данных:', error);
        if (refreshIcon) {
            refreshIcon.style.cssText = '';
            refreshIcon.textContent = '❌';
            setTimeout(() => {
                refreshIcon.textContent = '🔄';
            }, 2000);
        }
        if (refreshText) {
            refreshText.textContent = 'Ошибка';
            setTimeout(() => {
                refreshText.textContent = 'Обновить';
            }, 2000);
        }
    }
}

/**
 * Прикрепляет обработчики для таба Whitelist
 */
function attachWhitelistHandlers() {
    // Обработчик добавления пользователя
    const addButton = document.getElementById('admin-add-button');
    if (addButton && !addButton.hasAttribute('data-handler-attached')) {
        addButton.setAttribute('data-handler-attached', 'true');
        addButton.addEventListener('click', async () => {
            const telegramIdInput = document.getElementById('admin-add-telegram-id');
            const usernameInput = document.getElementById('admin-add-username');
            const isAdminCheckbox = document.getElementById('admin-add-is-admin');
            const messageDiv = document.getElementById('admin-add-message');
            
            const telegramId = telegramIdInput?.value?.trim();
            const username = usernameInput?.value?.trim() || null;
            const isAdmin = isAdminCheckbox ? (isAdminCheckbox.checked || false) : false;
            
            if (!telegramId) {
                if (messageDiv) {
                    messageDiv.innerHTML = '<span style="color: #ef4444;">Введите Telegram ID</span>';
                }
                return;
            }
            
            // Проверяем, является ли текущий пользователь супер-админом, если пытается добавить админа
            if (isAdmin) {
                // Получаем информацию о текущем пользователе
                const whitelistData = await getWhitelistUsers();
                const currentUser = whitelistData.currentUser || null;
                const isCurrentUserSuperAdmin = currentUser?.is_super_admin === true || currentUser?.is_super_admin === 'true';
                
                if (!isCurrentUserSuperAdmin) {
                    if (messageDiv) {
                        messageDiv.innerHTML = '<span style="color: #ef4444;">Только супер-администратор может добавлять администраторов</span>';
                    }
                    return;
                }
            }
            
            addButton.disabled = true;
            addButton.textContent = 'Добавление...';
            if (messageDiv) {
                messageDiv.innerHTML = '';
            }
            
            const result = await addUserToWhitelist(telegramId, username, isAdmin);
            
            if (result.success) {
                if (messageDiv) {
                    messageDiv.innerHTML = `<span style="color: #10b981;">${result.message || 'Пользователь добавлен'}</span>`;
                }
                if (telegramIdInput) telegramIdInput.value = '';
                if (usernameInput) usernameInput.value = '';
                if (isAdminCheckbox) isAdminCheckbox.checked = false;
                
                // Перезагружаем список
                setTimeout(() => {
                    renderAdminScreen();
                }, 500);
            } else {
                if (messageDiv) {
                    messageDiv.innerHTML = `<span style="color: #ef4444;">${result.message || 'Ошибка добавления'}</span>`;
                }
            }
            
            addButton.disabled = false;
            addButton.textContent = 'Добавить';
        });
    }
    
    // Обработчики изменения статуса админа
    const toggleAdminButtons = document.querySelectorAll('.admin-toggle-admin-button');
    toggleAdminButtons.forEach(button => {
        if (!button.hasAttribute('data-handler-attached')) {
            button.setAttribute('data-handler-attached', 'true');
            button.addEventListener('click', async () => {
                const telegramId = button.getAttribute('data-telegram-id');
                const action = button.getAttribute('data-action'); // 'add' или 'remove'
                if (!telegramId) return;
                
                const isAdding = action === 'add';
                const actionText = isAdding ? 'выдать права администратора' : 'снять права администратора';
                
                if (!confirm(`${isAdding ? 'Выдать' : 'Снять'} права администратора пользователю ${telegramId}?`)) {
                    return;
                }
                
                button.disabled = true;
                button.textContent = isAdding ? 'Выдача...' : 'Снятие...';
                
                // Находим пользователя в списке, чтобы получить его username
                const userCard = button.closest('div[style*="padding: 16px"]');
                const usernameElement = userCard?.querySelector('div[style*="font-weight: 600"]');
                const username = usernameElement?.textContent?.replace('@', '').replace(' 👑', '').trim() || null;
                
                const result = await addUserToWhitelist(telegramId, username, isAdding);
                
                if (result.success) {
                    // Перезагружаем список
                    setTimeout(() => {
                        renderAdminScreen();
                    }, 500);
                } else {
                    alert(result.message || 'Ошибка изменения статуса');
                    button.disabled = false;
                    button.textContent = isAdding ? 'Сделать админом' : 'Снять админку';
                }
            });
        }
    });
    
    // Обработчики удаления пользователей
    const removeButtons = document.querySelectorAll('.admin-remove-button');
    removeButtons.forEach(button => {
        if (!button.hasAttribute('data-handler-attached')) {
            button.setAttribute('data-handler-attached', 'true');
            button.addEventListener('click', async () => {
                const telegramId = button.getAttribute('data-telegram-id');
                if (!telegramId) return;
                
                if (!confirm(`Удалить пользователя ${telegramId} из whitelist?`)) {
                    return;
                }
                
                button.disabled = true;
                button.textContent = 'Удаление...';
                
                const result = await removeUserFromWhitelist(telegramId);
                
                if (result.success) {
                    // Перезагружаем список
                    setTimeout(() => {
                        renderAdminScreen();
                    }, 500);
                } else {
                    alert(result.message || 'Ошибка удаления');
                    button.disabled = false;
                    button.textContent = 'Удалить';
                }
            });
        }
    });
}

/**
 * Прикрепляет обработчики для таба Мониторинг
 */
function attachMonitoringHandlers() {
    const resetButton = document.getElementById('admin-reset-stats-button');
    if (resetButton && !resetButton.hasAttribute('data-handler-attached')) {
        resetButton.setAttribute('data-handler-attached', 'true');
        resetButton.addEventListener('click', async () => {
            if (!confirm('Сбросить статистику БД? Это действие нельзя отменить.')) {
                return;
            }
            
            resetButton.disabled = true;
            resetButton.textContent = 'Сброс...';
            
            const result = await resetDatabaseStats();
            
            if (result.success) {
                // Перезагружаем статистику
                const stats = await getDatabaseStats();
                const contentDiv = document.getElementById('admin-tab-content');
                if (contentDiv) {
                    contentDiv.innerHTML = renderMonitoringTab(stats);
                    attachMonitoringHandlers();
                }
            } else {
                alert(result.message || 'Ошибка сброса статистики');
            }
            
            resetButton.disabled = false;
            resetButton.textContent = '🔄 Сбросить';
        });
    }
}

/**
 * Прикрепляет обработчики для таба Попытки доступа
 */
function attachAccessAttemptsHandlers() {
    // Обработчик поиска
    const searchInput = document.getElementById('access-attempts-search');
    if (searchInput && !searchInput.hasAttribute('data-handler-attached')) {
        searchInput.setAttribute('data-handler-attached', 'true');
        searchInput.addEventListener('input', (e) => {
            const query = e.target.value.toLowerCase().trim();
            const items = document.querySelectorAll('.access-attempt-item');
            
            items.forEach(item => {
                const telegramId = item.getAttribute('data-telegram-id') || '';
                const username = item.getAttribute('data-username') || '';
                const ip = item.getAttribute('data-ip') || '';
                
                const matches = !query || 
                    telegramId.includes(query) || 
                    username.includes(query) || 
                    ip.includes(query);
                
                item.style.display = matches ? 'block' : 'none';
            });
        });
    }
    
    // Обработчики кнопок "Добавить в whitelist"
    const addButtons = document.querySelectorAll('.access-attempt-add-button');
    addButtons.forEach(button => {
        if (!button.hasAttribute('data-handler-attached')) {
            button.setAttribute('data-handler-attached', 'true');
            button.addEventListener('click', async () => {
                const telegramId = button.getAttribute('data-telegram-id');
                const username = button.getAttribute('data-username') || null;
                
                if (!telegramId) {
                    alert('Ошибка: не указан Telegram ID');
                    return;
                }
                
                if (!confirm(`Добавить пользователя ${telegramId}${username ? ' (@' + username + ')' : ''} в whitelist?`)) {
                    return;
                }
                
                button.disabled = true;
                button.textContent = 'Добавление...';
                
                const result = await addUserToWhitelist(telegramId, username, false);
                
                if (result.success) {
                    button.textContent = '✅ Добавлено';
                    button.style.background = '#10b981';
                    
                    // Обновляем список попыток доступа через 1 секунду
                    setTimeout(async () => {
                        const accessData = await getAccessAttempts();
                        const contentDiv = document.getElementById('admin-access-attempts-content');
                        if (contentDiv) {
                            contentDiv.innerHTML = renderAccessAttemptsTab(accessData);
                            attachAccessAttemptsHandlers();
                        }
                    }, 1000);
                } else {
                    alert(result.message || 'Ошибка добавления пользователя');
                    button.disabled = false;
                    button.textContent = '➕ Добавить в whitelist';
                }
            });
        }
    });
}

/**
 * Проверка, является ли текущий пользователь админом
 */
export async function isCurrentUserAdmin() {
    return await checkAdminStatus();
}
